import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchContextAnalysis,
  isTerminalContextAnalysisStatus,
  selectContextAnalysisForDocument,
} from "../slice/AISlice";

/**
 * Polls `GET /api/documents/<documentId>/context-analysis/` every
 * `intervalMs` while the cached row's BE status is non-terminal.
 *
 * Contract (mirrors usePollAnalysisJob — see that file for the full
 * design rationale; this hook is intentionally parallel so a future
 * abstraction can fold both):
 *   - Caller passes `documentId`. Hook is a no-op if missing.
 *   - First poll fires immediately so the UI shows fresh state right
 *     after the user lands on a doc — without it, the right-rail
 *     Context Analysis tab would render the stale cached state for up
 *     to 5 seconds before the first scheduled poll. (For the inbox
 *     case where the BE row may not exist yet, this also seeds the
 *     `pending`-state UI immediately on doc selection.)
 *   - Polls every `intervalMs` (default 5000ms — matches the BE
 *     handoff §6 polling cadence).
 *   - Stops as soon as the cached `data.status` becomes terminal
 *     (`completed | failed`). The slice's `null`-as-pending semantics
 *     keep the hook polling when the BE row hasn't been created yet
 *     (which the BE wraps as `data.status === "pending"`).
 *   - Pauses when `document.visibilityState === "hidden"` (user
 *     switched tabs / minimized window). Resumes immediately on the
 *     next `visibilitychange → visible`. Saves API load and sidesteps
 *     browser throttling of background `setInterval` (which can
 *     stretch to once-per-minute and surface as a fake "stuck"
 *     UX when the user comes back).
 *   - Cleans up on unmount or when `documentId`/`intervalMs` change.
 *     No leaked intervals across document switches.
 *
 * Why a hook (not middleware): same rationale as usePollAnalysisJob
 * — the visibility pause + per-mount lifecycle is easier to reason
 * about locally. Both surfaces (inbox right rail + matter-workspace
 * right rail) compose by mounting this hook with their active
 * documentId; the per-document slice cache means the two panes share
 * polling state when they happen to point at the same doc.
 */
export default function usePollContextAnalysis({
  documentId,
  intervalMs = 5000,
} = {}) {
  const dispatch = useDispatch();

  // Read the latest cached state so the polling tick can decide
  // whether to keep polling without restarting the interval on
  // every poll-fulfilled patch.
  const slot = useSelector((state) =>
    selectContextAnalysisForDocument(state, documentId)
  );
  const slotRef = useRef(slot);
  useEffect(() => {
    slotRef.current = slot;
  }, [slot]);

  const beStatus = slot?.data?.status || null;
  // Terminal at hook-effect-run time. The effect re-runs when
  // `beStatus` changes (driven by slice patches on poll-fulfilled),
  // so a row that becomes terminal cleanly tears down its interval.
  const terminal = isTerminalContextAnalysisStatus(beStatus);

  useEffect(() => {
    if (!documentId || terminal) return undefined;

    let cancelled = false;
    let intervalId = null;

    const poll = () => {
      if (cancelled) return;
      // Re-check terminal state from the ref before issuing a poll;
      // a poll can complete and patch the slice between scheduled
      // ticks, and the ref is the source of truth between effect
      // runs.
      const latest = slotRef.current?.data?.status;
      if (latest && isTerminalContextAnalysisStatus(latest)) {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }
      dispatch(fetchContextAnalysis(documentId));
    };

    const start = () => {
      if (intervalId !== null) return;
      // Fire immediately so the UI hydrates on first paint.
      poll();
      intervalId = setInterval(poll, intervalMs);
    };

    const stop = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const isVisible = () =>
      typeof document === "undefined" ||
      document.visibilityState !== "hidden";

    const handleVisibilityChange = () => {
      if (cancelled) return;
      const latest = slotRef.current?.data?.status;
      if (latest && isTerminalContextAnalysisStatus(latest)) {
        stop();
        return;
      }
      if (isVisible()) {
        start();
      } else {
        stop();
      }
    };

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    if (isVisible()) {
      start();
    }

    return () => {
      cancelled = true;
      stop();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
    };
  }, [dispatch, documentId, intervalMs, terminal]);
}
